import type { LucideIcon } from 'lucide-react';
import {
  Ban,
  Crown,
  Gavel,
  Shield,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { CONDUCT_ITEMS_SEED } from '@/lib/regras-seed-data';
import { stripHtmlToText } from '@/lib/cms-rich-helpers';

export type ConductCategoryId =
  | 'all'
  | 'responsabilidades'
  | 'moderacao'
  | 'seguranca'
  | 'banimentos'
  | 'proprietarios';

export type ConductAppearance = 'dark' | 'light';

export type ConductCategory = {
  id: ConductCategoryId;
  label: string;
};

export const CONDUCT_CATEGORIES: ConductCategory[] = [
  { id: 'all', label: 'Todos' },
  { id: 'responsabilidades', label: 'Responsabilidades' },
  { id: 'moderacao', label: 'Moderação' },
  { id: 'seguranca', label: 'Segurança' },
  { id: 'banimentos', label: 'Banimentos' },
  { id: 'proprietarios', label: 'Proprietários' },
];

export type ConductItemPresentation = {
  title: string;
  categoryId: Exclude<ConductCategoryId, 'all'>;
  /** Destaque visual para regras de maior peso. */
  emphasized: boolean;
  Icon: LucideIcon;
};

function normalizePlain(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Metadados visuais inferidos do texto seed — não alteram o conteúdo CMS. */
const SEED_PRESENTATION: ConductItemPresentation[] = [
  {
    title: 'Não inventar regras',
    categoryId: 'responsabilidades',
    emphasized: true,
    Icon: Gavel,
  },
  {
    title: 'Motivo em cada punição',
    categoryId: 'banimentos',
    emphasized: true,
    Icon: Ban,
  },
  {
    title: 'Proteção durante raids',
    categoryId: 'seguranca',
    emphasized: true,
    Icon: ShieldAlert,
  },
  {
    title: 'Clemência a quem busca ajuda',
    categoryId: 'moderacao',
    emphasized: false,
    Icon: Users,
  },
  {
    title: 'Explicar violações no kick',
    categoryId: 'moderacao',
    emphasized: false,
    Icon: Gavel,
  },
  {
    title: 'Consultar antes de rebaixar',
    categoryId: 'responsabilidades',
    emphasized: true,
    Icon: Shield,
  },
  {
    title: 'Banimentos justos e proporcionais',
    categoryId: 'banimentos',
    emphasized: true,
    Icon: Ban,
  },
  {
    title: 'Proprietários também ajudam',
    categoryId: 'proprietarios',
    emphasized: false,
    Icon: Crown,
  },
  {
    title: 'Indicar novos membros',
    categoryId: 'proprietarios',
    emphasized: false,
    Icon: Users,
  },
  {
    title: 'Revisar banimentos do bot',
    categoryId: 'banimentos',
    emphasized: true,
    Icon: ShieldAlert,
  },
  {
    title: 'Moderadores temporários',
    categoryId: 'proprietarios',
    emphasized: false,
    Icon: Crown,
  },
  {
    title: 'Relatos de usuários não registrados',
    categoryId: 'seguranca',
    emphasized: true,
    Icon: Shield,
  },
];

const SEED_BY_TEXT = new Map(
  CONDUCT_ITEMS_SEED.map((text, i) => [normalizePlain(text), SEED_PRESENTATION[i]!])
);

function fallbackTitle(plain: string, index: number): string {
  const first = plain.split(/[.!?]/)[0]?.trim() ?? '';
  if (first.length >= 12 && first.length <= 72) return first;
  if (first.length > 72) return `${first.slice(0, 69).trim()}…`;
  return `Diretriz ${String(index + 1).padStart(2, '0')}`;
}

function inferCategoryFromText(plain: string): Exclude<ConductCategoryId, 'all'> {
  const t = normalizePlain(plain);
  if (/raid|\/p\b|\/pr\b|segurança|bloquear|ignorar|não registrado/.test(t)) return 'seguranca';
  if (/baniment|expuls|mute|bot/.test(t)) return 'banimentos';
  if (/proprietári|tornasse um membro|moderador temporário/.test(t)) return 'proprietarios';
  if (/kick|clemência|regra|rebaixar|moder/.test(t)) return 'moderacao';
  return 'responsabilidades';
}

function inferEmphasis(plain: string, categoryId: Exclude<ConductCategoryId, 'all'>): boolean {
  const t = normalizePlain(plain);
  if (categoryId === 'seguranca' || categoryId === 'banimentos') return true;
  return /abuso|ditar regras|rebaixar|responsabilidade/.test(t);
}

function iconForCategory(categoryId: Exclude<ConductCategoryId, 'all'>): LucideIcon {
  switch (categoryId) {
    case 'seguranca':
      return ShieldAlert;
    case 'banimentos':
      return Ban;
    case 'proprietarios':
      return Crown;
    case 'moderacao':
      return Gavel;
    default:
      return Shield;
  }
}

/**
 * Inferência apenas visual (título curto, categoria, destaque).
 * O HTML original da regra permanece intacto na renderização.
 */
export function getConductItemPresentation(
  bodyHtml: string,
  index: number
): ConductItemPresentation {
  const plain = stripHtmlToText(bodyHtml);
  const seed = SEED_BY_TEXT.get(normalizePlain(plain));
  if (seed) return seed;

  const categoryId = inferCategoryFromText(plain);
  return {
    title: fallbackTitle(plain, index),
    categoryId,
    emphasized: inferEmphasis(plain, categoryId),
    Icon: iconForCategory(categoryId),
  };
}

export function formatConductIndex(index: number): string {
  return String(index + 1).padStart(2, '0');
}
